import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get("/cart");
      setItems(res.data.items || []);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addItem = async (product_id, quantity = 1, color = null, size = null) => {
    await api.post("/cart/items", { product_id, quantity, color, size });
    await refresh();
  };

  const updateItem = async (item_id, quantity) => {
    await api.put(`/cart/items/${item_id}`, { quantity });
    await refresh();
  };

  const removeItem = async (item_id) => {
    await api.delete(`/cart/items/${item_id}`);
    await refresh();
  };

  const clearCart = async () => {
    await api.post("/cart/clear");
    await refresh();
  };

  const count = items.reduce((sum, it) => sum + it.quantity, 0);
  const subtotal = items.reduce((sum, it) => sum + (it.product?.price || 0) * it.quantity, 0);

  const value = useMemo(
    () => ({ items, loading, count, subtotal, addItem, updateItem, removeItem, clearCart, refresh }),
    [items, loading, count, subtotal, refresh]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
